/**
 * @file OverviewBuyingSection.tsx
 * @description «Как покупает» на «Обзоре» клиента (phase19.31, SC68): что
 * берёт чаще, как получает и платит, как часто и не пропал ли.
 *
 * Строка показывается, только когда есть что сказать: «раз в N дней» — при
 * посчитанном промежутке, «Не покупал N дней» — если давно (правило в
 * `pauseText`). Покупок не было — одна спокойная строка.
 */

import { SectionCard } from "../../ui/SectionCard";
import type { CustomerOverviewBuyingDto } from "../../../lib/api-customers-overview";
import { countsText, frequencyText, pauseText, topProductsText } from "./overview-view";

interface Fact {
  key: string;
  label?: string;
  text: string;
  attention?: boolean;
}

function buyingFacts(buying: CustomerOverviewBuyingDto): Fact[] {
  const rows: (Fact | null)[] = [
    withText("top", "Чаще всего", topProductsText(buying)),
    withText("receive", "Получает", countsText(buying.receiveMethods)),
    withText("pay", "Платит", countsText(buying.paymentMethods)),
    withText("freq", undefined, frequencyText(buying)),
  ];
  const pause = pauseText(buying);
  if (pause) rows.push({ key: "pause", text: pause, attention: true });
  return rows.filter((row): row is Fact => row !== null);
}

function withText(key: string, label: string | undefined, text: string | null): Fact | null {
  return text ? { key, label, text } : null;
}

export function OverviewBuyingSection({ buying }: { buying: CustomerOverviewBuyingDto }) {
  const facts = buyingFacts(buying);
  return (
    <SectionCard title="Как покупает">
      {facts.length === 0 ? (
        <p className="type-body text-on-surface-variant">Покупок пока не было</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {facts.map((f) => (
            <li
              key={f.key}
              className={f.attention ? "type-body font-medium text-[var(--status-attention-fg)]" : "type-body break-words text-on-surface"}
            >
              {f.label ? <span className="text-on-surface-variant">{f.label}: </span> : null}
              {f.text}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
